<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { jumpToChatMessage } from 'dashboard/helper/jumpToChatMessage';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Staff-only pinned messages (custom_attributes.mh_pins, newest first). Like
// WhatsApp: one pin shows at a time; clicking jumps to it and moves on to the
// next, so repeated clicks walk through every pin.
const props = defineProps({
  chat: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const pins = computed(() => props.chat.custom_attributes?.mh_pins || []);
const index = ref(0);
const current = computed(() => pins.value[index.value % pins.value.length]);

watch(
  () => props.chat.id,
  () => {
    index.value = 0;
  }
);

const counter = computed(() =>
  pins.value.length > 1
    ? `${(index.value % pins.value.length) + 1}/${pins.value.length}`
    : ''
);
const preview = computed(() =>
  current.value.sender
    ? `${current.value.sender}: ${current.value.text}`
    : current.value.text
);

const pinnedByTip = computed(() =>
  current.value?.by_name
    ? t('CONVERSATION.PINNED_BAR.PINNED_BY', { name: current.value.by_name })
    : ''
);

const onOpen = () => {
  const pin = current.value;
  index.value = (index.value + 1) % pins.value.length;
  jumpToChatMessage(store, props.chat, pin.id);
};

const unpin = async () => {
  try {
    await store.dispatch('toggleMessagePin', {
      conversationId: props.chat.id,
      messageId: current.value.id,
      pinned: false,
    });
    index.value = 0;
  } catch {
    useAlert(t('CONVERSATION.CONTEXT_MENU.PIN_FAILED'));
  }
};
</script>

<template>
  <!-- Right gutter keeps unpin clear of the floating contact/store toggles. -->
  <div
    v-if="current"
    class="flex items-center gap-2 py-1.5 ltr:pl-4 ltr:pr-16 rtl:pr-4 rtl:pl-16 border-b border-n-weak bg-n-solid-2 min-w-0"
  >
    <Icon icon="i-lucide-pin" class="size-4 text-n-slate-11 shrink-0" />
    <button
      v-tooltip.bottom="pinnedByTip"
      type="button"
      class="flex flex-col items-start flex-1 min-w-0 text-start"
      @click="onOpen"
    >
      <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
        {{ t('CONVERSATION.PINNED_BAR.TITLE') }}
        <span
          v-if="current.private"
          class="px-1 rounded bg-n-amber-3 text-n-amber-11"
        >
          {{ t('CONVERSATION.PINNED_BAR.NOTE') }}
        </span>
        <span v-if="counter">{{ counter }}</span>
      </span>
      <span class="w-full text-sm truncate text-n-slate-12">
        {{ preview }}
      </span>
    </button>
    <button
      v-tooltip.left="t('CONVERSATION.CONTEXT_MENU.UNPIN')"
      type="button"
      class="p-1 rounded shrink-0 text-n-slate-11 hover:bg-n-alpha-2"
      @click="unpin"
    >
      <Icon icon="i-lucide-x" class="size-4" />
    </button>
  </div>
</template>
